import { Dispatch, SetStateAction, useCallback, useEffect } from 'react'

import { useSignup } from '@context/SignupContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { LeadDto } from '@model/LeadDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { useFetchOne } from '@hooks/useFetch'
import { useLocallyPersistedState } from '@hooks/useStorage'

import { validateAndSyncProductIds } from './productValidationUtils'

interface UseProducts {
  setSelectedProductIds: Dispatch<SetStateAction<TickmillProductType[]>>
  selectedProductIds: TickmillProductType[]
}

export const useProducts = (): UseProducts => {
  const { signupData } = useSignup()
  const [selectedProductIds, setSelectedProductIds] = useLocallyPersistedState<
    TickmillProductType[]
  >('selectedProductIdsKey', [])

  useEffect(() => {
    const leadProductIds = signupData.lead?.products?.map((p) => p.id) || []
    const signupContextProductIds = signupData.values?.productIds || []

    const validationResult = validateAndSyncProductIds(
      signupContextProductIds,
      selectedProductIds,
      leadProductIds
    )

    if (validationResult.shouldResetToLeadProducts) {
      setSelectedProductIds(leadProductIds)
      return
    }

    if (selectedProductIds.length === 0 && leadProductIds.length > 0) {
      setSelectedProductIds(leadProductIds)
    }
  }, [
    signupData.lead?.products,
    signupData.values?.productIds,
    selectedProductIds,
    setSelectedProductIds,
  ])

  return { selectedProductIds, setSelectedProductIds }
}

export const useLeadProducts = (lead?: LeadDto): void => {
  const { setSelectedProductIds } = useProducts()
  const apiClient = useApiClient(ClientApiClient)

  const callback = useCallback(async () => {
    if (!lead?.id || !lead?.token) {
      return
    }
    const products = await apiClient.getClientProductsByToken(lead?.id, lead?.token)

    if (products.length) {
      setSelectedProductIds(products.map((product) => product.id))
    }
  }, [apiClient, lead?.id, lead?.token, setSelectedProductIds])

  useFetchOne(callback)
}
